import React, { useEffect, useMemo, useRef, useState } from "react";
import Spinner from "react-bootstrap/Spinner";
import { pdf } from "@react-pdf/renderer";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagicWandSparkles, faTimes, faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import { handleTokenAndPostRequest } from "../../utils/utils";
import { buildCustomDocument, PROPERTY_QR_VARIABLE_DOCS } from "./customDesignRuntime";

const labelStyle = {
  display: "block",
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  marginBottom: 6,
};
const inputStyle = {
  width: "100%",
  padding: "10px 12px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  fontSize: 14,
  boxSizing: "border-box",
  outline: "none",
  background: "#fff",
};
const ghostBtn = {
  background: "transparent",
  color: "#2563eb",
  border: "1px solid #e5e7eb",
  borderRadius: 6,
  padding: "6px 12px",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
};

// Indeterminate progress bar. The generation request is one shot (~30–60s with
// claude-opus-4-7) — we don't stream — so the bar just animates while we wait.
function ProgressBar({ active }) {
  return (
    <div
      style={{
        height: 6,
        background: "#e5e7eb",
        borderRadius: 999,
        overflow: "hidden",
        position: "relative",
      }}
    >
      {active && (
        <div
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            width: "40%",
            background: "linear-gradient(90deg, #2563eb, #7c3aed)",
            borderRadius: 999,
            animation: "qr-design-progress 1.4s ease-in-out infinite",
          }}
        />
      )}
      <style>{`
        @keyframes qr-design-progress {
          0% { left: -40%; }
          100% { left: 100%; }
        }
      `}</style>
    </div>
  );
}

function VariablesPanel() {
  const [open, setOpen] = useState(false);
  const sections = [
    ["Agent", PROPERTY_QR_VARIABLE_DOCS.agent],
    ["Property", PROPERTY_QR_VARIABLE_DOCS.property],
    ["Derived / helpers", PROPERTY_QR_VARIABLE_DOCS.derived],
  ];
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 8, background: "#fff" }}>
      <button
        onClick={() => setOpen((x) => !x)}
        style={{
          width: "100%",
          background: "transparent",
          border: "none",
          padding: "10px 12px",
          textAlign: "left",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 8,
        }}
      >
        <span style={{ fontWeight: 700, fontSize: 13, color: "#111827" }}>
          Variables the AI can use ({sections.reduce((n, [, vs]) => n + vs.length, 0)})
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} style={{ color: "#6b7280" }} />
      </button>
      {open && (
        <div style={{ padding: "0 12px 12px", maxHeight: 240, overflowY: "auto" }}>
          {sections.map(([title, vars]) => (
            <div key={title} style={{ marginTop: 6 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 4 }}>
                {title}
              </div>
              {vars.map(([key, desc]) => (
                <div key={key} style={{ display: "flex", gap: 8, fontSize: 12, padding: "2px 0", lineHeight: 1.5 }}>
                  <code style={{ background: "#f3f4f6", padding: "1px 6px", borderRadius: 4, color: "#0f172a", fontSize: 11, whiteSpace: "nowrap" }}>
                    {key}
                  </code>
                  <span style={{ color: "#4b5563" }}>{desc}</span>
                </div>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Modal entrypoint.
//
// `mode === "new"`  → starting fresh from a built-in base design.
// `mode === "edit"` → editing an existing custom design (preloaded code/prompt).
//
// `sample` is the same {agent, properties[2]} object PropertyQrTab uses to
// preview the built-ins, so the modal can render a real PDF preview of the
// generated design using the same data shape it will see in production.
export default function CustomizeDesignModal({
  isMobile,
  mode,
  baseDesign,
  existingDesign,
  sample,
  agentFontFamily,
  initialPreviewUrl,
  onClose,
  onSaved,
}) {
  const [prompt, setPrompt] = useState(
    mode === "edit" ? existingDesign?.prompt || "" : ""
  );
  const [name, setName] = useState(
    mode === "edit" ? existingDesign?.name || "" : ""
  );
  const [code, setCode] = useState(
    mode === "edit" ? existingDesign?.code || "" : ""
  );
  // Seed with the parent's existing preview URL so the right pane shows the
  // starting-point design immediately. The URL belongs to the parent — we
  // never revoke it. Once `code` lands we render our own and forget this one.
  const [previewUrl, setPreviewUrl] = useState(initialPreviewUrl || null);
  const [previewOwned, setPreviewOwned] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const previewReqRef = useRef(0);

  // When `code` changes (initial load in edit-mode, or after a successful
  // generate), render a preview PDF. Old blob URLs are revoked.
  useEffect(() => {
    if (!code || !sample) return undefined;
    const reqId = ++previewReqRef.current;
    let revoked = false;
    (async () => {
      try {
        const colour = sample.agent.agent_colour || "#2563eb";
        // Mirror PropertyQrDocument's derivations so the preview matches.
        const hexToRgb = (hex) => {
          const h = (hex || "").replace("#", "").padEnd(6, "0").slice(0, 6);
          return {
            r: parseInt(h.slice(0, 2), 16),
            g: parseInt(h.slice(2, 4), 16),
            b: parseInt(h.slice(4, 6), 16),
          };
        };
        const darken = (hex, factor = 0.7) => {
          const { r, g, b } = hexToRgb(hex);
          const f = (n) => Math.max(0, Math.min(255, Math.round(n * factor)))
            .toString(16)
            .padStart(2, "0");
          return `#${f(r)}${f(g)}${f(b)}`;
        };
        const pickBannerText = (hex) => {
          const { r, g, b } = hexToRgb(hex);
          const lin = (c) => {
            const cc = c / 255;
            return cc <= 0.04045 ? cc / 12.92 : Math.pow((cc + 0.055) / 1.055, 2.4);
          };
          const lum = 0.2126 * lin(r) + 0.7152 * lin(g) + 0.0722 * lin(b);
          return lum > 0.4 ? "#1a1a1a" : "#ffffff";
        };
        const colourDark = darken(colour, 0.7);
        const bannerText = pickBannerText(colour);
        const firstName = (sample.agent.agent_name || "your agent").split(" ")[0];
        const inPersonCta = `Call ${firstName} on ${sample.agent.agent_phone || ""}`;
        const document = buildCustomDocument({
          code,
          agent: { ...sample.agent, agent_font_family: agentFontFamily },
          agentFontFamily,
          properties: sample.properties,
          copy: {
            agencyNameShort: "",
            headline: "See what this home is really worth, instantly",
            chip1: "Instant estimate",
            chip2: "100% free",
            chip3: "No sign-up",
            chip4: "Local insights",
            scanPrompt: "Scan with your phone camera",
            inPersonHeadline: "Prefer an in-person price update?",
            inPersonCta: "Call {name} on {phone}",
            noImageMode: "dark",
          },
          colour,
          colourDark,
          bannerText,
          inPersonCta,
        });
        const blob = await pdf(document).toBlob();
        if (reqId !== previewReqRef.current) return;
        const url = URL.createObjectURL(blob);
        setPreviewUrl((prev) => {
          // Only revoke URLs we created — the seeded one belongs to the parent.
          if (prev && previewOwned) URL.revokeObjectURL(prev);
          return url;
        });
        setPreviewOwned(true);
        setError("");
      } catch (e) {
        if (revoked) return;
        setError(e.message || "Couldn't render this design.");
        setPreviewUrl(null);
      }
    })();
    return () => {
      revoked = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, sample, agentFontFamily]);

  const handleGenerate = async () => {
    if (!prompt.trim() || generating) return;
    setGenerating(true);
    setError("");
    try {
      // Always carry forward the current in-memory preview as the starting
      // point once one exists — otherwise re-generating in "new" mode would
      // restart from the built-in's one-line description every time and
      // throw away the agent's previous tweaks.
      const baseId = mode === "edit" ? existingDesign.base_design_id : baseDesign.id;
      const baseName = mode === "edit" ? existingDesign.name : baseDesign.name;
      const body = code
        ? {
          base_design_id: baseId,
          base_design_name: baseName,
          previous_code: code,
          prompt,
        }
        : {
          base_design_id: baseId,
          base_design_name: baseName,
          base_design_description: baseDesign?.description || "",
          prompt,
        };
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/property-qr-design/generate`,
        body
      );
      if (res.data?.status !== "success" || !res.data?.code) {
        throw new Error(res.data?.message || "Generation failed.");
      }
      setCode(res.data.code);
      if (!name) {
        const baseName = mode === "edit" ? existingDesign.name : (baseDesign?.name || "Custom");
        setName(`${baseName} — custom`);
      }
    } catch (e) {
      setError(e.message || "Generation failed.");
    } finally {
      setGenerating(false);
    }
  };

  const handleSave = async () => {
    if (!code || !name.trim() || saving) return;
    setSaving(true);
    setError("");
    try {
      const body = {
        id: mode === "edit" ? existingDesign.id : undefined,
        name: name.trim(),
        base_design_id: mode === "edit" ? existingDesign.base_design_id : baseDesign.id,
        prompt,
        code,
      };
      const res = await handleTokenAndPostRequest(
        `${window.api_host}/property-qr-design/save`,
        body
      );
      if (res.data?.status !== "success") {
        throw new Error(res.data?.message || "Save failed.");
      }
      onSaved && onSaved({
        id: res.data.id,
        name: name.trim(),
        base_design_id: mode === "edit" ? existingDesign.base_design_id : baseDesign.id,
        prompt,
        code,
      });
    } catch (e) {
      setError(e.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const title = useMemo(() => {
    if (mode === "edit") return `Edit "${existingDesign?.name || "design"}"`;
    return `Customise "${baseDesign?.name || "design"}"`;
  }, [mode, baseDesign, existingDesign]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15, 23, 42, 0.72)",
        zIndex: 20000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: isMobile ? 8 : 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          width: "min(1100px, 96vw)",
          maxHeight: "96vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: 14,
            borderBottom: "1px solid #e5e7eb",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#111827" }}>{title}</div>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              Tell the AI how to tweak the design — colours, layout, copy, imagery, anything.
              The QR, agent name and address always stay on the page.
            </div>
          </div>
          <button onClick={onClose} style={{ ...ghostBtn, padding: "6px 10px" }}>
            <FontAwesomeIcon icon={faTimes} /> Close
          </button>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1fr) minmax(0, 1fr)",
            gap: 0,
          }}
        >
          {/* Left: prompt + variables + name */}
          <div style={{ padding: 16, overflowY: "auto", borderRight: isMobile ? "none" : "1px solid #e5e7eb" }}>
            <label style={labelStyle}>Describe the design</label>
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={
                "e.g. Make the agency colour the dominant element with a thick top band; move the QR to the very centre; use rounded corners; replace the chips with a single line that says 'Free property report' under the headline."
              }
              rows={isMobile ? 4 : 7}
              style={{ ...inputStyle, fontFamily: "inherit", resize: "vertical", minHeight: 110 }}
            />

            <div style={{ marginTop: 12 }}>
              <VariablesPanel />
            </div>

            <div style={{ marginTop: 12 }}>
              <button
                onClick={handleGenerate}
                disabled={!prompt.trim() || generating}
                style={{
                  background: !prompt.trim() || generating ? "#9ca3af" : "#7c3aed",
                  color: "#fff",
                  border: "none",
                  borderRadius: 8,
                  padding: "10px 18px",
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: !prompt.trim() || generating ? "not-allowed" : "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                {generating ? (
                  <>
                    <Spinner animation="border" size="sm" /> Generating with Claude Opus…
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faMagicWandSparkles} />
                    {code ? "Re-generate" : "Generate design"}
                  </>
                )}
              </button>
            </div>

            {generating && (
              <div style={{ marginTop: 10 }}>
                <ProgressBar active />
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                  Claude Opus is writing your design. This usually takes 20–60 seconds.
                </div>
              </div>
            )}

            {error && (
              <div
                style={{
                  marginTop: 12,
                  padding: 10,
                  background: "#fef2f2",
                  color: "#991b1b",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            )}

            {code && (
              <div style={{ marginTop: 16 }}>
                <label style={labelStyle}>Design name</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Bold mint header"
                  style={inputStyle}
                />
                <button
                  onClick={handleSave}
                  disabled={!name.trim() || saving}
                  style={{
                    marginTop: 10,
                    background: !name.trim() || saving ? "#9ca3af" : "#2563eb",
                    color: "#fff",
                    border: "none",
                    borderRadius: 8,
                    padding: "10px 18px",
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: !name.trim() || saving ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving…" : "Save design"}
                </button>
                <div style={{ fontSize: 11, color: "#6b7280", marginTop: 6 }}>
                  Saved designs appear for everyone in your agency.
                </div>
              </div>
            )}
          </div>

          {/* Right: live preview */}
          <div style={{ background: "#f3f4f6", display: "flex", flexDirection: "column" }}>
            <div style={{ padding: "10px 14px", borderBottom: "1px solid #e5e7eb", fontSize: 12, color: "#6b7280" }}>
              {code ? "Preview" : "Preview will appear here once the AI finishes."}
            </div>
            <div style={{ flex: 1, minHeight: isMobile ? 320 : 520 }}>
              {previewUrl ? (
                <iframe
                  title="Custom design preview"
                  src={previewUrl}
                  style={{ width: "100%", height: "100%", border: 0, background: "#f3f4f6" }}
                />
              ) : (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%",
                    color: "#9ca3af",
                    fontSize: 13,
                    padding: 24,
                    textAlign: "center",
                  }}
                >
                  {generating
                    ? "Drawing your design…"
                    : code
                      ? "Compiling…"
                      : "Type what you want to change, then hit Generate."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
